/**
 * Resolves dynamic Select options for backend-driven schema fields.
 *
 * Backend fields may declare:
 *
 *   optionsFrom: {
 *     resolver: 'custom_object_schema_fields',
 *     dependsOn: 'related_object_name',
 *   }
 *
 * The resolver runs in form context with access to the nested model instance
 * (sibling field values) and optional optionsContext from extendedFieldSchema.
 */

const resolvers = {};

export function registerSchemaOptionsResolver(name, resolver) {
  resolvers[name] = resolver;
}

export function resolveSchemaFieldOptions(fieldSchema, context = {}) {
  const optionsFrom = fieldSchema?.optionsFrom;
  if (!optionsFrom?.resolver) {
    return fieldSchema?.options || [];
  }

  const resolver = resolvers[optionsFrom.resolver];
  if (!resolver) {
    return fieldSchema?.options || [];
  }

  return resolver(context, optionsFrom) || [];
}

function getCustomObjectSchema(objectName, context) {
  if (!objectName) {
    return null;
  }

  const fromContext = context.optionsContext?.customObjectSchemas?.[objectName];
  if (fromContext) {
    return fromContext;
  }

  const store = context.store;
  if (!store) {
    return null;
  }

  const object = (store.getters['customObjects/all'] || []).find(
    (entry) => entry.name === objectName
  );

  return object?.schema || null;
}

registerSchemaOptionsResolver('custom_object_schema_fields', (context, config) => {
  const dependsOn = config.dependsOn || 'related_object_name';
  const instance = context.instance || {};
  const objectName = instance[dependsOn];
  const schema = getCustomObjectSchema(objectName, context);

  if (!schema) {
    return [];
  }

  const excludedTypes = new Set([
    'Relation',
    'NestedModel',
    'Image',
    'File',
    'Liquid',
  ]);

  return Object.entries(schema)
    .filter(([key]) => !key.startsWith('external_'))
    .filter(([, fieldConfig]) => !excludedTypes.has(fieldConfig?.type))
    .map(([key, fieldConfig]) => ({
      text: fieldConfig.title || key,
      value: key,
    }))
    .sort((a, b) => String(a.text).localeCompare(String(b.text)));
});

export default {
  registerSchemaOptionsResolver,
  resolveSchemaFieldOptions,
};
